import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Veriff } from '@veriff/js-sdk';
import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk';
import { useChat } from '@/hooks/useChat';
import AnalyticsGA from '@/analyticsGA';
import config from '@/config';

function useStableState(initial) {
  const [state, setState] = useState(initial);
  const stableSet = useCallback(v => setState(v), []);
  return [state, stableSet];
}

export const useVeriff = ({
  parentId,
  user,
  submitText,
  onSuccess,
  onClose,
  onError,
}) => {
  const { t } = useTranslation();

  const onSuccessRef = useRef(onSuccess);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const veriffFrameRef = useRef(null);
  const setChatVisibleRef = useRef(null);
  const submitBtnTextRef = useRef(submitText || t('veriff:start_verification'));

  const [chatVisible, setChatVisible] = useStableState(true);
  setChatVisibleRef.current = setChatVisible;
  useChat(chatVisible);

  useEffect(() => {
    submitBtnTextRef.current = submitText || t('veriff:start_verification');
  }, [t, submitText]);

  useEffect(() => {
    const veriff = Veriff({
      host: config.veriffHostURL,
      apiKey: config.veriffApiKey,
      parentId,
      onSession(error, response) {
        if (error) {
          console.error('[Veriff] Session error:', error);
          onErrorRef.current?.(error);
          return;
        }

        if (!response?.verification?.url) {
          console.error('[Veriff] Invalid session response:', response);
          onErrorRef.current?.(new Error('Invalid Veriff session response'));
          return;
        }

        setChatVisibleRef.current?.(false);
        sessionStorage.setItem(
          '@veriff-session-url',
          response.verification.url,
        );

        veriffFrameRef.current = createVeriffFrame({
          url: response.verification.url,
          onEvent(msg) {
            switch (msg) {
              case MESSAGES.STARTED:
                break;

              case MESSAGES.SUBMITTED:
                AnalyticsGA.veriffPass();
                onSuccessRef.current?.({ response, error, msg });
                break;

              case MESSAGES.FINISHED:
                setChatVisibleRef.current?.(true);
                sessionStorage.removeItem('@veriff-session-url');
                onCloseRef.current?.();
                break;

              case MESSAGES.CANCELED:
                setChatVisibleRef.current?.(true);
                sessionStorage.removeItem('@veriff-session-url');
                onCloseRef.current?.();
                break;

              default:
                break;
            }
          },
          onReload() {
            window.location.reload();
          },
        });
      },
    });

    veriff.setParams({
      person: {
        givenName: user.first_name || ' ',
        lastName: user.last_name || ' ',
      },
      vendorData: user.email || ' ',
    });

    veriff.mount({
      submitBtnText: submitBtnTextRef.current,
    });

    return () => {
      veriffFrameRef.current?.close();
      veriffFrameRef.current = null;
      sessionStorage.removeItem('@veriff-session-url');
      setChatVisibleRef.current?.(true);
    };

    // user намеренно исключён: instance создаётся один раз,
    // setParams вызывается с данными на момент монтирования
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);
};
